/*
  Summit Feedback hub (respondents).
  Users pick a workshop/session they attended from a dropdown (most attend
  3-5 of the ~25), and see/edit the entries they've already started below.
*/
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { usePermissions } from '../hooks/usePermissions';
import { get } from '../api/client';

const statusChip = (status) => {
  const styles = {
    submitted: { background: 'var(--cr-success, #2e7d32)', color: '#fff', label: 'Submitted' },
    draft: { background: '#b98a00', color: '#fff', label: 'Draft' },
    none: { background: 'var(--cr-border)', color: 'var(--cr-text)', label: 'Not started' },
  };
  const s = styles[status] || styles.none;
  return (
    <span style={{ background: s.background, color: s.color, borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null);

const SummitFeedbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWorkshopId, setSelectedWorkshopId] = useState('');
  const justSubmitted = location.state?.submitted;

  useEffect(() => {
    get('/api/summit-feedback/current').then(({ ok, data: d }) => {
      if (ok) setData(d);
      else setError('Failed to load summit feedback.');
      setLoading(false);
    }).catch(() => {
      setError('Failed to load summit feedback.');
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;

  const year = data?.year;
  const myResponses = data?.myResponses || {};
  const myOtherResponses = data?.myOtherResponses || [];
  const workshops = year?.workshops || [];
  const started = workshops.filter((w) => myResponses[String(w._id)]);
  const notStarted = workshops.filter((w) => !myResponses[String(w._id)]);

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title="Summit Feedback" section="leaders" />

      {error && <p style={{ color: 'var(--cr-error)' }}>{error}</p>}

      {justSubmitted && (
        <p style={{ background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '8px', padding: '0.75rem 1rem', fontWeight: 500 }}>
          Thank you! Your feedback was submitted. You can still edit it until feedback is captured.
        </p>
      )}

      {!year && !error && (
        <div style={{ background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '8px', padding: '1.25rem' }}>
          {data?.lastCaptured ? (
            <>
              <p style={{ margin: 0, fontWeight: 500 }}>
                Feedback for {data.lastCaptured.title || `Summit ${data.lastCaptured.year}`} has been captured and is now closed.
              </p>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--cr-text-muted)', fontSize: '0.95rem' }}>
                Thank you to everyone who shared their takeaways. Check back when the next summit opens.
              </p>
            </>
          ) : (
            <p style={{ margin: 0, color: 'var(--cr-text-muted)' }}>
              No summit feedback is open right now. Check back after the next Celebrate Recovery Summit.
            </p>
          )}
          {isAdmin && (
            <p style={{ margin: '0.75rem 0 0' }}>
              <Link to="/admin/summit-feedback">Manage summit years →</Link>
            </p>
          )}
        </div>
      )}

      {year && (
        <>
          <h2 style={{ fontSize: '1.2rem', margin: '0 0 0.25rem' }}>{year.title}</h2>
          <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.95rem', marginBottom: '1.25rem' }}>
            Pick a workshop or session you attended and share your feedback — just the ones you
            went to. You can edit your answers anytime until feedback is captured.
          </p>

          {(
            <div style={{ background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '10px', padding: '1rem 1.15rem', marginBottom: '1.5rem' }}>
              <label htmlFor="sf-workshop-select" style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
                Add feedback for a workshop or session
              </label>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                Attended something that isn&apos;t listed? Choose &quot;Other / not listed&quot; at the bottom.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                <select
                  id="sf-workshop-select"
                  value={selectedWorkshopId}
                  onChange={(e) => setSelectedWorkshopId(e.target.value)}
                  style={{
                    flex: '1 1 260px', minWidth: 0, padding: '0.5rem 0.6rem', borderRadius: '8px',
                    border: '1px solid var(--cr-border)', fontSize: '1rem', background: '#fff', color: 'var(--cr-text)',
                  }}
                >
                  <option value="">— Select a workshop or session —</option>
                  {notStarted.map((w) => (
                    <option key={w._id} value={w._id}>
                      {w.name}{w.speaker ? ` (${w.speaker})` : ''}
                    </option>
                  ))}
                  <option value="other">Other / not listed…</option>
                </select>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!selectedWorkshopId}
                  onClick={() => navigate(`/summit-feedback/${year._id}/workshop/${selectedWorkshopId}`)}
                >
                  Give feedback
                </button>
              </div>
            </div>
          )}

          {(started.length > 0 || myOtherResponses.length > 0) && (
            <>
              <h3 style={{ fontSize: '1.05rem', margin: '0 0 0.6rem' }}>Your feedback</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {myOtherResponses.map((o) => (
                  <li key={o.workshopId} style={{ marginBottom: '0.6rem' }}>
                    <Link
                      to={`/summit-feedback/${year._id}/workshop/${o.workshopId}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem',
                        background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '8px',
                        textDecoration: 'none', color: 'var(--cr-text)',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 600 }}>{o.topic || 'Other session'}</span>
                        <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                          {['Write-in', o.speaker].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      {statusChip(o.status || 'none')}
                    </Link>
                  </li>
                ))}
                {started.map((w) => {
                  const mine = myResponses[String(w._id)];
                  const dateStr = formatDate(w.sessionDate);
                  return (
                    <li key={w._id} style={{ marginBottom: '0.6rem' }}>
                      <Link
                        to={`/summit-feedback/${year._id}/workshop/${w._id}`}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem',
                          background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '8px',
                          textDecoration: 'none', color: 'var(--cr-text)',
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600 }}>{w.name}</span>
                          <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--cr-text-muted)' }}>
                            {[w.speaker, dateStr].filter(Boolean).join(' · ') || 'Tap to view or edit'}
                          </span>
                        </span>
                        {statusChip(mine?.status || 'none')}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {started.length === 0 && myOtherResponses.length === 0 && workshops.length > 0 && (
            <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>
              You haven&apos;t given any feedback yet — pick your first workshop above.
            </p>
          )}

          {workshops.length === 0 && (
            <p style={{ color: 'var(--cr-text-muted)' }}>No workshops have been added for this summit yet.</p>
          )}

          {isAdmin && (
            <p style={{ marginTop: '1.5rem' }}>
              <Link to="/admin/summit-feedback">Manage summit years →</Link>
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default SummitFeedbackPage;
